import React, { useRef } from "react";

import { FormField, FormLabel } from "@impero-com/ui";
import { DatePicker, DatePickerProps } from "antd";
import { DatePickRef } from "antd/es/date-picker/generatePicker/interface";
import { cx } from "class-variance-authority";
import moment, { Moment } from "moment-timezone";
import { RefCallBack } from "react-hook-form";

import { getParentElement } from "../../../utils/dom";
import { ERRORED_CLASSNAME } from "../../../utils/form/erroredElements";
import { DATE_FORMAT } from "../../../utils/formatting";
import { mergeRefs } from "../../../utils/mergeRefs";

import * as theme from "./theme.css";

type ValueType = Date | null;

export interface DatePickerInputBaseProps {
    allowClear?: boolean;
    label?: React.ReactNode;
    disabled?: boolean;
    disabledDate?: (date: Moment) => boolean;
    autoFocus?: boolean;
    format?: DatePickerProps["format"];
    // Should be (in) the scrollable container
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
    nodeAfter?: React.ReactNode;
}

export interface DatePickerInputProps<
    T extends ValueType,
> extends DatePickerInputBaseProps {
    value: T;
    error?: string | undefined;
    fieldRef?: RefCallBack;
    onChange: (value: T) => void;
}

export function DatePickerInput<T extends ValueType>({
    label,
    value,
    error,
    allowClear,
    onChange,
    autoFocus,
    nodeAfter,
    fieldRef,
    format = DATE_FORMAT,
    disabled = false,
    disabledDate,
    getPopupContainer = getParentElement,
}: DatePickerInputProps<T>) {
    const datePickerRef: DatePickRef<Date> = useRef(null);

    const handleChange = (value: Moment | null) => {
        if (value === null) {
            onChange(null as T);
        } else {
            onChange(value.toDate() as T);
        }
    };

    return (
        <FormField
            error={error}
            className={cx(error && ERRORED_CLASSNAME, theme.positionRelative)}
            lite={label === undefined}
        >
            {label && (
                <FormLabel onClick={() => datePickerRef.current?.focus()}>
                    {label}
                </FormLabel>
            )}
            <div className={theme.inputWithText}>
                <DatePicker
                    ref={mergeRefs(datePickerRef, fieldRef)}
                    value={value === null ? null : moment(value)}
                    disabled={disabled}
                    disabledDate={disabledDate}
                    allowClear={allowClear}
                    onChange={handleChange}
                    autoFocus={autoFocus}
                    format={format}
                    getPopupContainer={getPopupContainer}
                />
                {nodeAfter}
            </div>
        </FormField>
    );
}
